import React, { useState, useRef, useEffect, useMemo } from "react";
import styles from "./Scoreboard.module.css";
import { MatchData } from "../App";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Grid, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Colours, SamaggiTypography } from "../Components/SamaggiTypography";
import SamaggiPaper from "../Components/SamaggiPaper";
import theme from "../theme";
import { SamaggiButton } from "../Components/SamaggiButton";


interface StaffProps {
	matches: MatchData[];
	updateMatch: (updatedMatch: MatchData) => void;
	elapsedTime: number;
	setElapsedTime: React.Dispatch<React.SetStateAction<number>>;
	isRunning: boolean;
	setIsRunning: React.Dispatch<React.SetStateAction<boolean>>;
	refreshMatches: () => Promise<void>;
}

const ADVANCE_ALLOWED_GROUPS = new Set(["quarterfinals", "semifinals"]);

const Staff: React.FC<StaffProps> = (props) => {
	// const [selectedMatch, setSelectedMatch] = useState<MatchData | null>(null);
	const [selectedMatch, setSelectedMatch] = useState<MatchData | null>(() => {
		const searchParams = new URLSearchParams(window.location.search);
		const matchIdFromURL = searchParams.get("match");

		return matchIdFromURL
			? props.matches.find(match => match.match_id === matchIdFromURL) || null
			: null;
	});
	const [sportFilter, setSportFilter] = useState<string>("");
	const [categoryFilter, setCategoryFilter] = useState<string>("");
	const [timeFilter, setTimeFilter] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [knockoutStageFilter, setKnockoutStageFilter] = useState<string>("");
	const [showAllMatches, setShowAllMatches] = useState<boolean>(false);
	const [quarterOrSet, setQuarterOrSet] = useState(selectedMatch?.quarterOrSet ?? 0);
	const [fouls1, setFouls1] = useState(selectedMatch?.fouls1 ?? 0);
	const [fouls2, setFouls2] = useState(selectedMatch?.fouls2 ?? 0);
	const [elapsedTime, setElapsedTime] = useState(0); // Starts from 0 seconds
	const [isRunning, setIsRunning] = useState(false);
	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const navigate = useNavigate();
	const location = useLocation();
	const [badmintonSets, setBadmintonSets] = useState<MatchData[]>([]);
	const [confirmEndOpen, setConfirmEndOpen] = useState(false);
	const [team1Replacement, setTeam1Replacement] = useState("");
	const [team2Replacement, setTeam2Replacement] = useState("");

	useEffect(() => {
		if (props.matches.length > 0 && !selectedMatch) {
			setSelectedMatch(props.matches[0]);
			setTeam1Replacement(props.matches[0].team1Name || "");
			setTeam2Replacement(props.matches[0].team2Name || "");
		}
	}, [props.matches, selectedMatch]);

	useEffect(() => {
		if (!selectedMatch) return;
		setTeam1Replacement(selectedMatch.team1Name || "");
		setTeam2Replacement(selectedMatch.team2Name || "");
	}, [selectedMatch?.match_id]);

	const API_URL = process.env.REACT_APP_API_URL;

	const isLikelyPlaceholder = (teamName: string) => {
		const value = (teamName || "").trim();
		if (!value) return true;
		return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?:-\d+)?$/i.test(value);
	};

	const availableUniversities = useMemo(() => {
		return Array.from(
			new Set(
				props.matches
					.flatMap((match) => [match.team1Name, match.team2Name])
					.map((name) => (name || "").trim())
					.filter((name) => name.length > 0 && !isLikelyPlaceholder(name))
			)
		).sort((a, b) => a.localeCompare(b));
	}, [props.matches]);

	const getTeamOptions = (currentTeam: string) => {
		const trimmedCurrent = (currentTeam || "").trim();
		if (!trimmedCurrent) return availableUniversities;
		return [trimmedCurrent, ...availableUniversities.filter((team) => team !== trimmedCurrent)];
	};

	const isManualUpdateEligible = true;

	const sendUpdateToBackend = async (type: string, value: number | String, team: "team1" | "team2" | null) => {
		if (!selectedMatch) return;

		console.log("PAUSE")

		try {
			const response = await axios.post(`${API_URL}/submit-event`, {
				"token": localStorage.getItem("token"),
				"type": type,
				"match-id": selectedMatch.match_id,
				"value": value,
				"team": team,
				"status": type === "status" ? true : undefined
			});

			console.log("PAUSE")

			if (response.status === 200) {
				console.log("Update successful:", response.data);
				return response.data?.data ?? null;
			} else {
				console.error("Failed to update:", response.data);
				alert("Error updating match data.");
				return null;
			}
		} catch (error) {
			// props.setPauseUpdates(false);
			console.error("Error sending update:", error);
			alert("Error updating match data.");
			return null;
		}
	};


	const handleMatchSelection = (match: MatchData) => {
		console.log("🔹 handleMatchSelection called with match:", match);
		window.scrollTo({ top: 0, behavior: "auto" });
		setSelectedMatch(match);
		setTeam1Replacement(match.team1Name || "");
		setTeam2Replacement(match.team2Name || "");

		// Update URL with selected match ID
		const searchParams = new URLSearchParams(location.search);
		searchParams.set("match", match.match_id);
		navigate(`?${searchParams.toString()}`, { replace: true });

		// If it's badminton, extract sets
		if (match.sport.startsWith("Badminton") && match.match_id.length > 36) {
			const baseId = match.match_id.slice(0, 36); // Extract base ID
			const allSets = props.matches.filter(m => m.match_id.startsWith(baseId));

			console.log("🔹 Available Sets for Badminton:", allSets);
			setBadmintonSets(allSets);

			// Automatically select set `-1` when first clicked
			const firstSet = allSets.find(m => m.match_id.endsWith("-1"));
			if (firstSet) {
				setSelectedMatch(firstSet);
			}
		} else {
			setBadmintonSets([]);
		}
	};



	const updateSet = (newSet: number) => {
		if (!selectedMatch) return;

		if (selectedMatch.match_id.length > 36) {
			const baseId = selectedMatch.match_id.slice(0, 36); // Extract base ID
			const newSetMatch = props.matches.find(m =>
				m.match_id.startsWith(baseId) && m.match_id.endsWith(`-${newSet}`)
			);

			if (newSetMatch && newSetMatch.match_id !== selectedMatch.match_id) {
				console.log("🔹 Switched to Set:", newSetMatch);
				setSelectedMatch(newSetMatch);

				// ✅ Update URL so it does not reset after 3 sec
				const searchParams = new URLSearchParams(location.search);
				searchParams.set("match", newSetMatch.match_id);
				navigate(`?${searchParams.toString()}`, { replace: true });
			} else {
				console.warn("⚠️ No match found for set", newSet);
			}
		}
	};


	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const matchIdFromURL = searchParams.get("match");

		if (matchIdFromURL) {
			const matchFromProps = props.matches.find(match => match.match_id === matchIdFromURL);

			if (matchFromProps && matchFromProps.match_id !== selectedMatch?.match_id) {
				setSelectedMatch(matchFromProps);
			}
		}
	}, [props.matches, location.search]);  // Removed `selectedMatch` to avoid resetting

	useEffect(() => {
		console.log("Staff component mounted");
	}, []);



	const updateScore = (team: "team1" | "team2", amount: number) => {
		if (!selectedMatch) return;


		if ((team === "team1" && selectedMatch.score1 === 0 && amount === -1) ||
			(team === "team2" && selectedMatch.score2 === 0 && amount === -1)) {
			return;
		}

		if (selectedMatch.status === "Playing") {
			const updatedScore = team === "team1" ? selectedMatch.score1 + amount : selectedMatch.score2 + amount;

			sendUpdateToBackend("score", amount, team);

			const updatedMatch: MatchData = {
				...selectedMatch,
				score1: team === "team1" ? Math.max(0, updatedScore) : selectedMatch.score1,
				score2: team === "team2" ? Math.max(0, updatedScore) : selectedMatch.score2,
			};

			props.updateMatch(updatedMatch);
			setSelectedMatch(updatedMatch);
		}
	};

	const updateMatchData = (updatedFields: Partial<MatchData>) => {
		if (!selectedMatch) return;

		const updatedMatch: MatchData = {
			...selectedMatch,
			...updatedFields,
		};

		props.updateMatch(updatedMatch);
		setSelectedMatch(updatedMatch);
	};

	const updateFouls = (team: "team1" | "team2", newFouls: number) => {
		if (!selectedMatch) return;

		sendUpdateToBackend("foul", newFouls, team);

		const updatedMatch: MatchData = {
			...selectedMatch,
			fouls1: team === "team1" ? newFouls + selectedMatch.fouls1 : selectedMatch.fouls1,
			fouls2: team === "team2" ? newFouls + selectedMatch.fouls2 : selectedMatch.fouls2
		};

		props.updateMatch(updatedMatch);
		setSelectedMatch(updatedMatch);
	};

	const increaseFouls1 = () => updateFouls("team1", 1);
	const decreaseFouls1 = () => updateFouls("team1", -1);

	const increaseFouls2 = () => updateFouls("team2", 1);
	const decreaseFouls2 = () => updateFouls("team2", -1);

	const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));

	const updateQuarterOrSet = (newQuarterOrSet: number) => {
		if (!selectedMatch) return;

		sendUpdateToBackend("quarterOrSet", newQuarterOrSet, null);

		const updatedMatch: MatchData = { ...selectedMatch, quarterOrSet: newQuarterOrSet + selectedMatch.quarterOrSet };
		props.updateMatch(updatedMatch);
		setSelectedMatch(updatedMatch);
	};

	const increaseQuarterOrSet = () => updateQuarterOrSet(1);
	const decreaseQuarterOrSet = () => updateQuarterOrSet(-1);

	const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!selectedMatch) return;

		const newStatus = e.target.value;

		sendUpdateToBackend("status", newStatus, null).then(_ => { }); // Send status update

		const updatedMatch: MatchData = {
			...selectedMatch,
			status: newStatus,
		};

		props.updateMatch(updatedMatch);
		setSelectedMatch(updatedMatch);
	};

	const handleEndMatchClick = () => {
		if (!selectedMatch) return;
		setConfirmEndOpen(true);
	};

	const confirmEndMatch = async () => {
		if (!selectedMatch) return;

		if (selectedMatch.status === "Ended") {
			alert("This match is already ended.");
			setConfirmEndOpen(false);
			return;
		}

		const normalizedGroup = (selectedMatch.group || "").trim().toLowerCase();
		const requiresAdvance = ADVANCE_ALLOWED_GROUPS.has(normalizedGroup);

		if (requiresAdvance && selectedMatch.score1 === selectedMatch.score2) {
			alert("Cannot mark this match as Ended with a draw in this stage. A winner is required for propagation.");
			return;
		}

		const result = await sendUpdateToBackend("status", "Ended", null);
		if (!result) return;

		const updatedMatch: MatchData = {
			...selectedMatch,
			status: "Ended",
		};

		props.updateMatch(updatedMatch);
		setSelectedMatch(updatedMatch);
		setConfirmEndOpen(false);

		const advanceResult = result.advanceResult;
		if (advanceResult?.statusCode >= 400) {
			alert(advanceResult?.error || advanceResult?.message || "Match ended, but team propagation failed.");
		} else {
			const advanceMessage = advanceResult?.data?.message || advanceResult?.message;
			if (advanceMessage) {
				alert(advanceMessage);
			}
		}

		await props.refreshMatches();
	};

	const applyMatchUpdateFromDropdown = async (team: "team1" | "team2", newTeamName: string) => {
		if (!selectedMatch || !newTeamName) return;

		const prevTeamName = team === "team1" ? selectedMatch.team1Name : selectedMatch.team2Name;
		if (prevTeamName === newTeamName) return;

		try {
			const response = await axios.post(`${API_URL}/apply-match-update`, {
				token: localStorage.getItem("token"),
				"match-id": selectedMatch.match_id,
				"prev-team-name": prevTeamName,
				"new-team-name": newTeamName,
			});

			const updatedMatch: MatchData = {
				...selectedMatch,
				team1Name: team === "team1" ? newTeamName : selectedMatch.team1Name,
				team2Name: team === "team2" ? newTeamName : selectedMatch.team2Name,
			};

			props.updateMatch(updatedMatch);
			setSelectedMatch(updatedMatch);

			const message = response?.data?.data?.message || "Match updated successfully.";
			alert(message);
			await props.refreshMatches();
		} catch (error: any) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to apply match update.";
			alert(message);
			setTeam1Replacement(selectedMatch.team1Name || "");
			setTeam2Replacement(selectedMatch.team2Name || "");
		}
	};


	const filteredMatches = props.matches
		.filter((match: MatchData) => {
			const sportOk = !sportFilter || match.sport.toLowerCase().includes(sportFilter.toLowerCase());
			const categoryOk = !categoryFilter || match.sport.includes(categoryFilter);
			const statusOk = !statusFilter || match.status.includes(statusFilter);
			const stageText = `${match.group || ""} ${match.stage || ""}`.toLowerCase();
			const stageOk = !knockoutStageFilter || stageText.includes(knockoutStageFilter);

			return sportOk && categoryOk && statusOk && stageOk;
		})
		.reduce((uniqueMatches, match) => {
			if (match.sport.startsWith("Badminton") && match.match_id.length > 36) {
				const baseId = match.match_id.slice(0, 36);

				if (match.match_id.endsWith("-1") && !uniqueMatches.some(m => m.match_id.startsWith(baseId))) {
					uniqueMatches.push(match);
				}
			}
			else {
				uniqueMatches.push(match);
			}
			return uniqueMatches;
		}, [] as MatchData[]);



	return (
		<div style={{
			paddingLeft: !isLargeScreen ? "20%" : "25px",
			paddingRight: !isLargeScreen ? "20%" : "25px",
			paddingTop: "40px"
		}}>
			<Grid container direction={"column"} rowSpacing={3}>
				{/* main match display*/}
				<Grid item>
					<SamaggiPaper maxWidth wide noMargin colour={"rgb(0, 41, 44)"}>
						<Grid container direction={"column"} rowSpacing={4}>
							<Grid item>
								<SamaggiTypography bold xlarge center
									colour={Colours.white}>{selectedMatch?.sport?.toUpperCase()}</SamaggiTypography>
							</Grid>

							{/* Badminton Set Selector - Only shows if the match is Badminton */}
							{selectedMatch?.sport.startsWith("Badminton") && badmintonSets.length > 0 && (
								<Grid item container direction={"column"} rowSpacing={2}>
									<Grid item>
										<SamaggiTypography center colour={Colours.white} >Select Set:</SamaggiTypography>
									</Grid>
									<Grid item container direction={"row"} columnSpacing={2}>
										{badmintonSets.map((setMatch, index) => (
											<Grid item xs={parseInt((12 / badmintonSets.length).toFixed(0))}>
												<SamaggiButton
													key={setMatch.match_id}
													onClick={() => updateSet(index + 1)} // Switch between -1, -2, -3
													fullWidth
												>
													Set {index + 1}
												</SamaggiButton>
											</Grid>
										))}
									</Grid>
								</Grid>
							)}

							<Grid item container direction={"row"}>
								<Grid item container direction={"column"} xs={5}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center large
											bold>{selectedMatch ? selectedMatch.team1Name : "Team A"}</SamaggiTypography>
									</Grid>
									{isManualUpdateEligible && selectedMatch && (
										<Grid item style={{ display: "flex", justifyContent: "center" }}>
											<select
												className={styles.dropdown}
												style={{ minWidth: "220px", maxWidth: "100%" }}
												value={team1Replacement}
												onChange={(e) => {
													const newTeam = e.target.value;
													setTeam1Replacement(newTeam);
													applyMatchUpdateFromDropdown("team1", newTeam).then(() => { });
												}}
											>
												{getTeamOptions(selectedMatch.team1Name).map((teamName) => (
													<option key={`team1-${teamName}`} value={teamName}>{teamName}</option>
												))}
											</select>
										</Grid>
									)}
									<Grid item>
										<SamaggiTypography colour={Colours.white} center large
											bold>{selectedMatch ? selectedMatch.score1 : "0"}</SamaggiTypography>
									</Grid>
									<Grid item container direction={"row"} spacing={5}>
										<Grid item xs={6}>
											<SamaggiButton fullWidth onClick={() => updateScore("team1", -1)}
												disabled={selectedMatch?.score1 === 0}>-1
											</SamaggiButton>
										</Grid>
										<Grid item xs={6}>
											<SamaggiButton fullWidth
												onClick={() => updateScore("team1", 1)}>+1
											</SamaggiButton>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={2}>
									<SamaggiTypography colour={Colours.white} center large>V</SamaggiTypography>
								</Grid>
								<Grid item container direction={"column"} xs={5}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center large
											bold>{selectedMatch ? selectedMatch.team2Name : "Team B"}</SamaggiTypography>
									</Grid>
									{isManualUpdateEligible && selectedMatch && (
										<Grid item style={{ display: "flex", justifyContent: "center" }}>
											<select
												className={styles.dropdown}
												style={{ minWidth: "220px", maxWidth: "100%" }}
												value={team2Replacement}
												onChange={(e) => {
													const newTeam = e.target.value;
													setTeam2Replacement(newTeam);
													applyMatchUpdateFromDropdown("team2", newTeam).then(() => { });
												}}
											>
												{getTeamOptions(selectedMatch.team2Name).map((teamName) => (
													<option key={`team2-${teamName}`} value={teamName}>{teamName}</option>
												))}
											</select>
										</Grid>
									)}
									<Grid item>
										<SamaggiTypography colour={Colours.white} center large
											bold>{selectedMatch ? selectedMatch.score2 : "0"}</SamaggiTypography>
									</Grid>
									<Grid item container direction={"row"} spacing={5}>
										<Grid item xs={6}>
											<SamaggiButton fullWidth onClick={() => updateScore("team2", -1)}
												disabled={selectedMatch?.score1 === 0}>-1
											</SamaggiButton>
										</Grid>
										<Grid item xs={6}>
											<SamaggiButton fullWidth
												onClick={() => updateScore("team2", 1)}>+1
											</SamaggiButton>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
							<Grid item container direction={"row"} rowSpacing={3} columnSpacing={3}>
								{selectedMatch?.sport.startsWith("Badminton") && (
									<Grid item container direction={"column"} xs={6} lg={3} rowSpacing={2}>
										<Grid item>
											<SamaggiTypography bold center colour={Colours.white}>Current Set:</SamaggiTypography>
										</Grid>
										<Grid item>
											<SamaggiTypography bold center colour={Colours.white}>{selectedMatch ? selectedMatch.match_id.split("-").pop() : "1"}</SamaggiTypography>
										</Grid>
									</Grid>
								)}
								{!selectedMatch?.sport.startsWith("Badminton") && (
									<Grid item container direction={"column"} xs={6} lg={3} rowSpacing={2}>
										<Grid item>
											<SamaggiTypography colour={Colours.white} center>Quarter:</SamaggiTypography>
										</Grid>
										<Grid item container direction={"row"}>
											<Grid item xs={5}>
												<SamaggiButton fullWidth onClick={decreaseQuarterOrSet} className={styles.adjustButton}>-1
												</SamaggiButton>
											</Grid>
											<Grid item xs={2}>
												<SamaggiTypography
													colour={Colours.white} center bold>{selectedMatch?.quarterOrSet}</SamaggiTypography>
											</Grid>
											<Grid item xs={5}>
												<SamaggiButton fullWidth onClick={increaseQuarterOrSet} className={styles.adjustButton}>+1
												</SamaggiButton>
											</Grid>
										</Grid>
									</Grid>
								)}
								<Grid item container direction={"column"} xs={6} lg={3} rowSpacing={2}>
									<Grid item container direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
										<Grid item>
											<SamaggiTypography bold center colour={Colours.white}>Status</SamaggiTypography>
										</Grid>
										<Grid item>
											<SamaggiButton onClick={handleEndMatchClick}>
												Ended
											</SamaggiButton>
										</Grid>
									</Grid>
									<Grid item>
										<select
											className={styles.dropdown}
											onChange={handleStatusChange}
											value={selectedMatch?.status}
										>
											<option value="Not Started">Not Started</option>
											<option value="Paused">Paused</option>
											<option value="Soon">Starts Soon</option>
											<option value="Playing">Playing</option>
										</select>
									</Grid>
								</Grid>
								<Grid item container direction={"column"} xs={6} lg={3} rowSpacing={2}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center>Fouls
											({selectedMatch?.team1Name})</SamaggiTypography>
									</Grid>
									<Grid item container direction={"row"}>
										<Grid item xs={5}>
											<SamaggiButton fullWidth onClick={decreaseFouls1} className={styles.adjustButton}>-1</SamaggiButton>
										</Grid>
										<Grid item xs={2}>
											<SamaggiTypography colour={Colours.white} center bold>{selectedMatch?.fouls1}</SamaggiTypography>
										</Grid>
										<Grid item xs={5}>
											<SamaggiButton fullWidth onClick={increaseFouls1} className={styles.adjustButton}>+1</SamaggiButton>
										</Grid>
									</Grid>
								</Grid>
								<Grid item container direction={"column"} xs={6} lg={3} rowSpacing={2}>
									<Grid item>
										<SamaggiTypography bold center colour={Colours.white}>Fouls
											({selectedMatch?.team2Name})</SamaggiTypography>
									</Grid>
									<Grid item container direction={"row"}>
										<Grid item xs={5}>
											<SamaggiButton fullWidth onClick={decreaseFouls2}>-1</SamaggiButton>
										</Grid>
										<Grid item xs={2}>
											<SamaggiTypography colour={Colours.white} center bold>{selectedMatch?.fouls2}</SamaggiTypography>
										</Grid>
										<Grid item xs={5}>
											<SamaggiButton fullWidth onClick={increaseFouls2}>+1</SamaggiButton>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</SamaggiPaper>
					{/* status and timer */}
				</Grid>

				<Grid item container direction={"row"} rowSpacing={3}>
					<Grid item lg={6} xs={12}>
						<SamaggiTypography large bold>Filter Matches</SamaggiTypography>
					</Grid>
					<Grid item lg={6} xs={12}>
						<div className={styles.filterGroup}>
							{/* sports filter */}
							<select
								className={styles.dropdown}
								value={sportFilter}
								onChange={(e) => setSportFilter(e.target.value)}
							>
								<option value="">All Sports</option>
								<option value="Badminton">Badminton</option>
								<option value="Basketball">Basketball</option>
								<option value="Chairball">Chairball</option>
								<option value="Football">Football</option>
								<option value="Volleyball">Volleyball</option>
							</select>

							{/* category filter */}
							<select
								className={styles.dropdown}
								value={categoryFilter}
								onChange={(e) => setCategoryFilter(e.target.value)}
							>
								<option value="">All Categories</option>
								<option value="Male">Male</option>
								<option value="Female">Female</option>
								<option value="Mixed">Mixed</option>
							</select>

							{/* time filter */}
							<select
								className={styles.dropdown}
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
							>
								<option value="">All Statuses</option>
								<option value="Not Started">Not Started</option>
								<option value="Playing">Playing</option>
								<option value="Paused">Paused</option>
								<option value="Ended">Ended</option>
							</select>

							<select
								className={styles.dropdown}
								value={knockoutStageFilter}
								onChange={(e) => setKnockoutStageFilter(e.target.value)}
							>
								<option value="">All Stages</option>
								<option value="quarter">Quarter</option>
								<option value="semi">Semi</option>
								<option value="grand">Grand</option>
							</select>
						</div>
					</Grid>
				</Grid>

				{/* Matches List */}
				<Grid item container direction={"column"} rowSpacing={4} sx={{ marginTop: 0 }}>
					<Grid item>
						<SamaggiTypography bold large center>ALL MATCHES</SamaggiTypography>
					</Grid>
					<Grid item container direction={"row"} columnSpacing={2} rowSpacing={2}>
						{filteredMatches
							.slice(0, showAllMatches ? filteredMatches.length : 6)
							.map((match, index) => (
								<Grid item key={match.match_id} onClick={() => handleMatchSelection(match)} lg={3} md={4} xs={6}>
									<SamaggiPaper noPad noMargin>
										<div style={{ padding: "10px", paddingTop: "20px" }}>
											<Grid container direction={"column"} rowSpacing={3}>
												<Grid item>
													<SamaggiTypography bold center large>{match.sport}</SamaggiTypography>
												</Grid>
												<Grid item container direction={"row"} columnSpacing={2}>
													<Grid item xs={12} xl={5}>
														<SamaggiTypography bold center>{match.team1Name}</SamaggiTypography>
													</Grid>
													<Grid item xs={12} xl={2}>
														<SamaggiTypography center> vs </SamaggiTypography>
													</Grid>
													<Grid item xs={12} xl={5}>
														<SamaggiTypography bold center>{match.team2Name}</SamaggiTypography>
													</Grid>
												</Grid>
												<Grid item>
													<SamaggiTypography center bold xlarge>{match.score1} - {match.score2}</SamaggiTypography>
												</Grid>
												<Grid item>
													<SamaggiTypography center>{match.startTime}</SamaggiTypography>
												</Grid>
												<Grid item>
													<SamaggiTypography center>{match.location}</SamaggiTypography>
												</Grid>
												<Grid item>
													<SamaggiTypography center bold={match.status === "Playing" || match.status === "Starts Soon"} colour={match.status === "Playing" ? Colours.green : ((match.status === "Not Started" || match.status === "Ended") ? Colours.black : Colours.red)}>{match.status}</SamaggiTypography>
												</Grid>
												<Grid item>
													<SamaggiButton fullWidth onClick={(e) => { e.stopPropagation(); handleMatchSelection(match); }}>View</SamaggiButton>
												</Grid>
											</Grid>
										</div>
									</SamaggiPaper>

								</Grid>
							))}
					</Grid>

					<Grid item style={{ display: "flex", justifyContent: "center", alignItems: "center" }} xs={12}>
						<SamaggiButton onClick={() => setShowAllMatches(!showAllMatches)} paddingX={"20px"} paddingY={"10px"}>
							{showAllMatches ? "Hide Matches" : "View All Matches"}
						</SamaggiButton>
					</Grid>
				</Grid>
			</Grid>

			<Dialog open={confirmEndOpen} onClose={() => setConfirmEndOpen(false)}>
				<DialogTitle>Confirm End Match</DialogTitle>
				<DialogContent>
					<SamaggiTypography>Are you sure you want to mark this match as Ended?</SamaggiTypography>
				</DialogContent>
				<DialogActions>
					<SamaggiButton onClick={() => setConfirmEndOpen(false)}>No</SamaggiButton>
					<SamaggiButton onClick={confirmEndMatch}>Yes</SamaggiButton>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default Staff;
